import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Calendar, ChevronLeft, ChevronRight, Clock, RefreshCw, ArrowLeft, Filter, X, LayoutGrid, List, GripVertical, User, MapPin, Phone, UserPlus, Check, AlertCircle, Search, Users, Plus, Bell } from "lucide-react";
import { RdvCard, DayCell } from "./components/calendrier/CalendrierComponents";
import { DayPlanningView, WeekPlanningView } from "./components/calendrier/CalendrierPlanningView";
import AddRappelModal from "./components/AddRappelModal";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Composant de recherche avec autocomplétion pour les intervenants
function IntervenantSearch({ value, onChange, intervenants, placeholder = "Rechercher un collaborateur..." }) {
  const [inputValue, setInputValue] = useState(value || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);
  
  // Combiner tous les intervenants
  const allIntervenants = useMemo(() => {
    const all = [];
    if (intervenants.techniciens) {
      intervenants.techniciens.forEach(t => all.push({ name: t, type: 'Technicien' }));
    }
    if (intervenants.architectes) {
      intervenants.architectes.forEach(a => all.push({ name: a, type: 'Architecte' }));
    }
    if (intervenants.pose) {
      intervenants.pose.forEach(p => all.push({ name: p, type: 'Équipe Pose' }));
    }
    return all;
  }, [intervenants]);
  
  // Filtrer les suggestions
  useEffect(() => {
    if (inputValue.length >= 1) {
      const query = inputValue.toLowerCase();
      const filtered = allIntervenants.filter(i => 
        i.name.toLowerCase().includes(query)
      ).slice(0, 10);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, allIntervenants]);
  
  const handleSelect = (item) => {
    setInputValue(item.name);
    onChange(item.name);
    setShowSuggestions(false);
  };
  
  const handleClear = () => {
    setInputValue("");
    onChange("");
    setShowSuggestions(false);
  };
  
  // Fermer les suggestions au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={inputRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => filteredSuggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-8 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          data-testid="filter-intervenant-search"
        />
        {inputValue && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Dropdown suggestions */}
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {filteredSuggestions.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full px-4 py-2 text-left hover:bg-purple-600/30 transition-colors border-b border-slate-700 last:border-b-0 flex items-center justify-between"
            >
              <span className="text-white">{item.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                item.type === 'Technicien' ? 'bg-blue-500/20 text-blue-400' :
                item.type === 'Architecte' ? 'bg-purple-500/20 text-purple-400' :
                'bg-amber-500/20 text-amber-400'
              }`}>
                {item.type}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CalendrierDashboard({ onBack, embedded = false, onViewClient, authUser }) {
  const [rdvs, setRdvs] = useState([]);
  const [intervenants, setIntervenants] = useState({ techniciens: [], architectes: [], pose: [] });
  const [equipes, setEquipes] = useState([]); // Liste des équipes disponibles
  const [allTeams, setAllTeams] = useState([]); // Toutes les équipes depuis /api/teams
  const [teamMembers, setTeamMembers] = useState({}); // Membres de chaque équipe { teamId: [members] }
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week-planning"); // day, day-planning, week, week-planning, month
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState("");
  const [filterIntervenant, setFilterIntervenant] = useState("");
  const [filterEquipe, setFilterEquipe] = useState(""); // Nouveau filtre par équipe
  const [showFilters, setShowFilters] = useState(false);
  const [rdvTimes, setRdvTimes] = useState({}); // Stockage local des heures modifiées
  
  // Mode "Mon Planning" - affiche uniquement les RDV de l'utilisateur connecté
  const [myPlanningMode, setMyPlanningMode] = useState(false);
  const [userIdentifiers, setUserIdentifiers] = useState([]); // Identifiants de l'utilisateur pour filtrer ses RDV
  const [userAccessibleTeams, setUserAccessibleTeams] = useState([]); // Équipes accessibles avec niveaux de permission
  
  // État pour le client en attente de RDV
  const [pendingClient, setPendingClient] = useState(null);
  const [showCreateRdvModal, setShowCreateRdvModal] = useState(null); // { date, time }
  const [creatingRdv, setCreatingRdv] = useState(false);
  const [selectedEquipeForRdv, setSelectedEquipeForRdv] = useState("technicien"); // Équipe sélectionnée pour le RDV
  const [selectedTechnicien, setSelectedTechnicien] = useState("");
  
  // État pour le modal "Ajouter un RDV"
  const [showAddRdvModal, setShowAddRdvModal] = useState(false);
  const [searchClientQuery, setSearchClientQuery] = useState("");
  const [searchClientResults, setSearchClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  
  // État pour les rappels
  const [rappels, setRappels] = useState([]);
  
  // État pour le modal d'ajout de rappel (Mon Planning)
  const [showAddRappelModal, setShowAddRappelModal] = useState(false);
  const [selectedSlotForRappel, setSelectedSlotForRappel] = useState(null); // { date, time }

  // Charger le client en attente depuis sessionStorage
  useEffect(() => {
    const pending = window.sessionStorage.getItem('pendingRdvClient');
    if (pending) {
      try {
        setPendingClient(JSON.parse(pending));
      } catch (e) {
        console.error("Erreur parsing pendingRdvClient:", e);
      }
    }
  }, []);

  // Charger les identifiants de l'utilisateur pour le mode "Mon Planning"
  useEffect(() => {
    const loadUserIdentifiers = async () => {
      if (!authUser?.email) return;
      try {
        const resp = await authFetch(`${API}/api/user/planning?user_email=${encodeURIComponent(authUser.email)}`);
        if (resp.ok) {
          const data = await resp.json();
          setUserIdentifiers(data.user_identifiers || []);
          console.log("👤 Identifiants utilisateur pour Mon Planning:", data.user_identifiers);
        } else {
          console.error("Erreur API user/planning:", resp.status);
        }
      } catch (e) {
        console.error("Erreur chargement identifiants utilisateur:", e);
      }
    };
    loadUserIdentifiers();
  }, [authUser?.email]);

  // Charger les équipes accessibles avec permissions
  useEffect(() => {
    const loadAccessibleTeams = async () => {
      if (!authUser?.email) return;
      try {
        const resp = await authFetch(`${API}/api/teams/accessible?user_email=${encodeURIComponent(authUser.email)}`);
        if (resp.ok) {
          const data = await resp.json();
          setUserAccessibleTeams(data.teams || []);
          console.log("📅 Équipes accessibles:", data.teams?.map(t => `${t.name}: ${t.access_level}`));
        }
      } catch (e) {
        console.error("Erreur chargement équipes accessibles:", e);
      }
    };
    loadAccessibleTeams();
  }, [authUser?.email]);

  // Charger les rappels de l'utilisateur
  useEffect(() => {
    const loadRappels = async () => {
      if (!authUser?.email) return;
      try {
        const resp = await authFetch(`${API}/api/webhook/rappels/semaine?telepro_email=${encodeURIComponent(authUser.email)}`);
        if (resp.ok) {
          const data = await resp.json();
          setRappels(data.rappels || []);
          console.log("📞 Rappels chargés:", data.rappels?.length || 0);
        }
      } catch (e) {
        console.error("Erreur chargement rappels:", e);
      }
    };
    loadRappels();
    // Rafraîchir les rappels toutes les 2 minutes
    const interval = setInterval(loadRappels, 120000);
    return () => clearInterval(interval);
  }, [authUser?.email]);

  // Recherche de clients pour le modal "Ajouter RDV"
  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setSearchClientResults([]);
      return;
    }
    setSearchingClients(true);
    try {
      const resp = await authFetch(`${API}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: query })
      });
      if (resp.ok) {
        const data = await resp.json();
        setSearchClientResults(data.clients?.slice(0, 10) || []);
      }
    } catch (e) {
      console.error("Erreur recherche clients:", e);
    }
    setSearchingClients(false);
  };

  // Sélectionner un client pour le RDV
  const selectClientForRdv = (client) => {
    // Stocker dans sessionStorage comme "client en attente"
    const clientData = {
      row_number: client.row_number,
      nom: client.data?.['CLIENT NOM'] || client.data?.['NOM'] || '',
      prenom: client.data?.['PRÉNOM'] || client.data?.['PRENOM'] || '',
      telephone: client.data?.['TEL CLIENT'] || client.data?.['TEL'] || '',
      adresse: client.data?.['ADRESSE'] || '',
      ville: client.data?.['VILLE'] || ''
    };
    window.sessionStorage.setItem('pendingRdvClient', JSON.stringify(clientData));
    setPendingClient(clientData);
    setShowAddRdvModal(false);
    setSearchClientQuery("");
    setSearchClientResults([]);
  };

  // Annuler le client en attente
  const cancelPendingClient = () => {
    window.sessionStorage.removeItem('pendingRdvClient');
    setPendingClient(null);
  };

  // Créer le RDV pour le client en attente
  const createRdvForPendingClient = async () => {
    if (!pendingClient || !showCreateRdvModal || !selectedTechnicien) return;
    
    setCreatingRdv(true);
    try {
      // Appel API pour créer le RDV dans le Google Sheet
      const res = await authFetch(`${API}/api/calendrier/rdv/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowNumber: pendingClient.rowNumber,
          date: showCreateRdvModal.date, // Format YYYY-MM-DD
          heure: showCreateRdvModal.time,
          technicien: selectedTechnicien,
          equipe: selectedEquipeForRdv, // Équipe sélectionnée (technicien, architecte, pose)
          client_nom: pendingClient.nom,
          client_prenom: pendingClient.prenom,
          adresse: pendingClient.adresse,
          cp: pendingClient.cp,
          ville: pendingClient.ville,
          telephone: pendingClient.telephone
        })
      });
      
      const data = await res.json();
      if (data.success) {
        // Succès - nettoyer et recharger
        window.sessionStorage.removeItem('pendingRdvClient');
        setPendingClient(null);
        setShowCreateRdvModal(null);
        setSelectedTechnicien("");
        setSelectedEquipeForRdv("technicien");
        loadRdv(); // Recharger les RDV
      } else {
        alert("Erreur: " + (data.error || "Impossible de créer le RDV"));
      }
    } catch (e) {
      console.error("Erreur création RDV:", e);
      alert("Erreur lors de la création du RDV");
    }
    setCreatingRdv(false);
  };

  // Handler pour le clic sur un créneau
  // - Si un client est en attente: créer un RDV
  // - Si mode "Mon Planning" sans client: créer un rappel
  const handleSlotClick = (date, time) => {
    if (pendingClient) {
      setShowCreateRdvModal({ date, time });
      // Pré-sélectionner l'intervenant si défini
      if (pendingClient.intervenant) {
        setSelectedTechnicien(pendingClient.intervenant);
      }
    } else if (myPlanningMode) {
      // En mode Mon Planning, clic sur un créneau = créer un rappel
      setSelectedSlotForRappel({ date, time });
      setShowAddRappelModal(true);
    }
  };

  const loadRdv = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("type_rdv", filterType);
      if (filterIntervenant) params.append("intervenant", filterIntervenant);
      if (filterEquipe) params.append("equipe", filterEquipe);
      const resp = await authFetch(`${API}/api/calendrier/rdv?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (resp.ok) {
        const data = await resp.json();
        // Ajouter les heures stockées localement
        const rdvsWithTimes = (data.rdv || []).map((r, idx) => ({
          ...r,
          heure: rdvTimes[r.id] || r.heure || generateDefaultTime(idx, r.type)
        }));
        setRdvs(rdvsWithTimes);
        setIntervenants(data.intervenants || { techniciens: [], architectes: [], pose: [] });
        setEquipes(data.equipes || ['Techniciens', 'Architectes', 'Équipe Pose']);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Générer une heure par défaut
  const generateDefaultTime = (index, type) => {
    const baseHours = type === "R1" ? [9, 10, 11, 14, 15, 16, 17] : [9, 11, 14, 16];
    const hour = baseHours[index % baseHours.length];
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  useEffect(() => { loadRdv(); }, [filterType, filterIntervenant, filterEquipe]);

  // Charger toutes les équipes et leurs membres depuis /api/teams
  // Filtrer selon les flags de visibilité pour le planning ET les permissions utilisateur
  useEffect(() => {
    const loadTeamsAndMembers = async () => {
      try {
        // Si l'utilisateur est connecté, utiliser l'endpoint avec permissions
        let visibleTeams = [];
        
        if (authUser?.email) {
          // Charger les équipes accessibles avec permissions
          const resp = await authFetch(`${API}/api/teams/accessible?user_email=${encodeURIComponent(authUser.email)}`);
          if (resp.ok) {
            const data = await resp.json();
            // Ne garder que les équipes avec accès read ou write
            visibleTeams = (data.teams || []).filter(team => 
              team.access_level === "read" || team.access_level === "write"
            );
            setUserAccessibleTeams(data.teams || []);
          }
        } else {
          // Fallback: charger toutes les équipes visibles (pour mode embedded sans auth)
          const resp = await authFetch(`${API}/api/teams`);
          if (resp.ok) {
            const data = await resp.json();
            visibleTeams = (data.teams || []).filter(team => team.visible_planning !== false);
          }
        }
        
        setAllTeams(visibleTeams);
        
        // Charger les membres de chaque équipe accessible
        const members = {};
        for (const team of visibleTeams) {
          try {
            const membersResp = await authFetch(`${API}/api/teams/${team.id}/members`);
            if (membersResp.ok) {
              const membersData = await membersResp.json();
              members[team.id] = membersData.members || [];
            }
          } catch (e) { /* ignore */ }
        }
        setTeamMembers(members);
      } catch (e) { console.error("Erreur chargement équipes:", e); }
    };
    loadTeamsAndMembers();
  }, [authUser?.email]);

  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date()); };
  
  const goPrev = () => {
    const d = new Date(currentDate);
    if (viewMode.includes("day")) d.setDate(d.getDate() - 1);
    else if (viewMode.includes("week")) d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };
  
  const goNext = () => {
    const d = new Date(currentDate);
    if (viewMode.includes("day")) d.setDate(d.getDate() + 1);
    else if (viewMode.includes("week")) d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };
  
  // Handler pour voir la fiche complète d'un client
  // Peut recevoir un rowNumber (number) ou un objet { telephone: "..." }
  const handleViewFullProfile = async (param) => {
    if (!onViewClient) return;
    
    // Si c'est directement un rowNumber
    if (typeof param === 'number') {
      onViewClient(param);
      return;
    }
    
    // Si c'est un objet avec telephone, chercher le client
    if (param?.telephone) {
      try {
        const resp = await authFetch(`${API}/api/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ telephone: param.telephone.replace(/\s/g, '') })
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.clients && data.clients.length > 0) {
            const client = data.clients[0];
            if (client.row_number) {
              onViewClient(client.row_number);
              return;
            }
          }
        }
        // Client non trouvé dans Google Sheets - c'est peut-être un lead pas encore qualifié
        alert('Ce prospect n\'est pas encore dans le pipeline client. Qualifiez-le d\'abord pour créer sa fiche.');
      } catch (e) {
        console.error("Erreur recherche client par téléphone:", e);
      }
    }
  };

  // Calcul du début de semaine (Lundi)
  const weekStart = useMemo(() => {
    const start = new Date(currentDate);
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    start.setHours(0, 0, 0, 0);
    return start;
  }, [currentDate]);

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const days = [];
    for (let i = 0; i < 42; i++) {
      days.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const start = new Date(weekStart);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  // Filtrer les RDV selon le mode "Mon Planning"
  const filteredRdvs = useMemo(() => {
    if (!myPlanningMode || userIdentifiers.length === 0) {
      return rdvs;
    }
    // Filtrer uniquement les RDV où l'utilisateur est l'intervenant
    return rdvs.filter(r => {
      const intervenant = (r.intervenant || r.technicien || "").toLowerCase().trim();
      // Si pas d'intervenant, ne pas inclure le RDV
      if (!intervenant) return false;
      
      return userIdentifiers.some(id => {
        const idLower = id.toLowerCase().trim();
        // L'intervenant doit contenir l'identifiant ou vice-versa
        // Mais ignorer les correspondances avec chaîne vide
        if (!idLower) return false;
        return intervenant.includes(idLower) || idLower.includes(intervenant);
      });
    });
  }, [rdvs, myPlanningMode, userIdentifiers]);

  const rdvByDate = useMemo(() => {
    const map = {};
    filteredRdvs.forEach(r => {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    });
    return map;
  }, [filteredRdvs]);

  const formatDateKey = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getRdvForDate = (d) => rdvByDate[formatDateKey(d)] || [];
  const selectedRdvs = useMemo(() => getRdvForDate(viewMode.includes("day") ? currentDate : selectedDate), [selectedDate, currentDate, viewMode, rdvByDate]);
  
  const formatDate = (d) => d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const formatMonth = (d) => d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const formatWeek = (d) => {
    const endWeek = new Date(d);
    endWeek.setDate(endWeek.getDate() + 6);
    return `${d.getDate()} - ${endWeek.getDate()} ${endWeek.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;
  };
  
  const isToday = (d) => d.toDateString() === new Date().toDateString();
  const isCurrentMonth = (d) => d.getMonth() === currentDate.getMonth();

  const stats = useMemo(() => {
    const todayKey = formatDateKey(new Date());
    const todayRdvs = rdvByDate[todayKey] || [];
    const startOfWeek = new Date();
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
    let weekCount = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      weekCount += (rdvByDate[formatDateKey(d)] || []).length;
    }
    // Utiliser filteredRdvs pour les stats du mois (respecter le filtre "Mon Planning")
    const monthRdvs = filteredRdvs.filter(r => {
      const rd = new Date(r.date);
      return rd.getMonth() === currentDate.getMonth() && rd.getFullYear() === currentDate.getFullYear();
    });
    return {
      today: todayRdvs.length,
      week: weekCount,
      month: monthRdvs.length,
      r1: monthRdvs.filter(r => r.type === "R1").length,
      r2: monthRdvs.filter(r => r.type === "R2").length
    };
  }, [filteredRdvs, rdvByDate, currentDate]);

  // Handler pour le déplacement de RDV (drag & drop)
  const handleRdvMove = useCallback(async (rdv, newDate, newTime) => {
    console.log(`📅 Déplacement RDV ${rdv.id}: ${rdv.date} ${rdv.heure} → ${newDate} ${newTime}`);
    
    // Mettre à jour localement
    setRdvTimes(prev => ({ ...prev, [rdv.id]: newTime }));
    
    // Mettre à jour la liste des RDV
    setRdvs(prev => prev.map(r => {
      if (r.id === rdv.id) {
        return { ...r, date: newDate, heure: newTime };
      }
      return r;
    }));
    
    // Appeler l'API pour persister le changement
    try {
      const resp = await authFetch(`${API}/api/calendrier/rdv/${rdv.id}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowNumber: rdv.rowNumber,
          newDate: newDate,
          newTime: newTime,
          type: rdv.type
        })
      });
      
      if (!resp.ok) {
        console.error('Erreur lors du déplacement du RDV');
        // Recharger pour revenir à l'état correct
        loadRdv();
      }
    } catch (e) {
      console.error('Erreur API:', e);
    }
  }, [loadRdv]);

  // Handler pour le redimensionnement de RDV (changement de durée)
  const handleRdvResize = useCallback(async (rdv, newDuration) => {
    console.log(`⏱️ Redimensionnement RDV ${rdv.id}: ${rdv.duration || 60}min → ${newDuration}min`);
    
    // Mettre à jour localement
    setRdvs(prev => prev.map(r => {
      if (r.id === rdv.id) {
        return { ...r, duration: newDuration };
      }
      return r;
    }));
    
    // TODO: Appeler l'API pour persister la durée si nécessaire
    // Pour l'instant, la durée est stockée localement uniquement
  }, []);

  // Handler pour la suppression de RDV - mise à jour immédiate de l'UI
  const handleRdvDeleted = useCallback((deletedRdv) => {
    if (deletedRdv?.id) {
      // Retirer immédiatement le RDV de l'état local
      console.log(`🗑️ Suppression RDV ${deletedRdv.id} de l'affichage`);
      setRdvs(prev => prev.filter(r => r.id !== deletedRdv.id));
    }
    // Recharger aussi pour synchroniser avec le backend
    loadRdv();
  }, []);

  // Déterminer le titre de la période
  const getPeriodTitle = () => {
    if (viewMode.includes("day")) return formatDate(currentDate);
    if (viewMode.includes("week")) return formatWeek(weekStart);
    return formatMonth(currentDate);
  };

  return (
    <div className={`${embedded ? "" : "min-h-screen bg-slate-900"} p-4`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {onBack && <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400"><ArrowLeft className="w-6 h-6" /></button>}
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Calendar className="w-8 h-8 text-amber-400" />
                Calendrier RDV
              </h1>
              <p className="text-slate-400">Glissez-déposez les RDV pour les repositionner</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowAddRdvModal(true)}
              className="px-4 py-2 rounded-lg flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
              data-testid="add-rdv-btn"
            >
              <UserPlus className="w-4 h-4" />Ajouter un RDV
            </button>
            {/* Bouton Mon Planning */}
            {authUser && (
              <button 
                onClick={() => setMyPlanningMode(!myPlanningMode)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${
                  myPlanningMode 
                    ? "bg-blue-600 text-white ring-2 ring-blue-400" 
                    : "bg-slate-700 hover:bg-slate-600 text-slate-200"
                }`}
                data-testid="my-planning-btn"
                title={myPlanningMode ? "Voir tous les plannings" : "Afficher uniquement mes RDV"}
              >
                <User className="w-4 h-4" />
                {myPlanningMode ? "Mon Planning ✓" : "Mon Planning"}
              </button>
            )}
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${showFilters ? "bg-amber-600" : "bg-slate-700"} text-white`}
            >
              <Filter className="w-4 h-4" />Filtres
            </button>
            <button onClick={loadRdv} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg">
              <RefreshCw className={`w-5 h-5 text-slate-300 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Filtres */}
        {showFilters && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Type RDV</label>
              <select 
                value={filterType} 
                onChange={e => setFilterType(e.target.value)} 
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="filter-type-rdv"
              >
                <option value="">Tous</option>
                <option value="r1">R1 uniquement</option>
                <option value="r2">R2 uniquement</option>
              </select>
            </div>
            
            <div>
              <label className="text-xs text-slate-400 block mb-1">Équipe</label>
              <select 
                value={filterEquipe} 
                onChange={e => setFilterEquipe(e.target.value)} 
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="filter-equipe"
              >
                <option value="">Toutes les équipes</option>
                {equipes.map(eq => (
                  <option key={eq} value={eq}>{eq}</option>
                ))}
              </select>
            </div>
            
            <div className="min-w-[250px]">
              <label className="text-xs text-slate-400 block mb-1">Rechercher un collaborateur</label>
              <IntervenantSearch
                value={filterIntervenant}
                onChange={setFilterIntervenant}
                intervenants={intervenants}
                placeholder="Nom du collaborateur..."
              />
            </div>
            
            {(filterType || filterIntervenant || filterEquipe) && (
              <button 
                onClick={() => { setFilterType(""); setFilterIntervenant(""); setFilterEquipe(""); }} 
                className="px-3 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm flex items-center gap-1 hover:bg-red-600/30"
                data-testid="reset-filters-btn"
              >
                <X className="w-4 h-4" />Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4"><p className="text-2xl font-bold text-amber-400">{stats.today}</p><p className="text-sm text-amber-300">Aujourd'hui</p></div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4"><p className="text-2xl font-bold text-blue-400">{stats.week}</p><p className="text-sm text-blue-300">Cette semaine</p></div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4"><p className="text-2xl font-bold text-green-400">{stats.month}</p><p className="text-sm text-green-300">Ce mois</p></div>
          <div className="bg-blue-600/10 border border-blue-600/30 rounded-xl p-4"><p className="text-2xl font-bold text-blue-500">{stats.r1}</p><p className="text-sm text-blue-400">R1 ce mois</p></div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4"><p className="text-2xl font-bold text-purple-400">{stats.r2}</p><p className="text-sm text-purple-300">R2 ce mois</p></div>
        </div>

        {/* Bannière client en attente de RDV */}
        {pendingClient && (
          <div className="bg-emerald-500/10 border-2 border-emerald-500/50 rounded-xl p-4 mb-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/30 rounded-full flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 font-bold text-lg">Client en attente de RDV</p>
                  <p className="text-white font-medium">{pendingClient.nom} {pendingClient.prenom}</p>
                  <p className="text-sm text-slate-400">
                    {pendingClient.adresse}, {pendingClient.cp} {pendingClient.ville}
                    {pendingClient.telephone && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); window.ringoverDial && window.ringoverDial(pendingClient.telephone); }}
                        className="ml-2 text-emerald-400 hover:text-emerald-300 hover:underline"
                      >
                        📞 {pendingClient.telephone}
                      </button>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-emerald-300 text-sm font-medium">👆 Cliquez sur un créneau horaire</p>
                  <p className="text-slate-400 text-xs">pour créer le RDV</p>
                </div>
                <button
                  onClick={cancelPendingClient}
                  className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400 transition-colors"
                  title="Annuler"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation et vues */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <button onClick={goPrev} className="p-2 hover:bg-slate-700 rounded-lg"><ChevronLeft className="w-5 h-5 text-slate-300" /></button>
              <button onClick={goNext} className="p-2 hover:bg-slate-700 rounded-lg"><ChevronRight className="w-5 h-5 text-slate-300" /></button>
              <button onClick={goToday} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm">Aujourd'hui</button>
              <h2 className="text-xl font-semibold text-white ml-4 capitalize">{getPeriodTitle()}</h2>
            </div>
            
            {/* Sélecteur de vue */}
            <div className="flex gap-1 bg-slate-700 rounded-lg p-1">
              <button 
                data-testid="view-day-planning"
                onClick={() => setViewMode("day-planning")} 
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${viewMode === "day-planning" ? "bg-amber-600 text-white" : "text-slate-300"}`}
                title="Vue jour avec planning horaire"
              >
                <Clock className="w-4 h-4" />Jour
              </button>
              <button 
                data-testid="view-week-planning"
                onClick={() => setViewMode("week-planning")} 
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${viewMode === "week-planning" ? "bg-amber-600 text-white" : "text-slate-300"}`}
                title="Vue semaine avec planning horaire"
              >
                <LayoutGrid className="w-4 h-4" />Semaine
              </button>
              <button 
                data-testid="view-week-simple"
                onClick={() => setViewMode("week")} 
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${viewMode === "week" ? "bg-amber-600 text-white" : "text-slate-300"}`}
                title="Vue semaine simple"
              >
                <List className="w-4 h-4" />Simple
              </button>
              <button 
                data-testid="view-month"
                onClick={() => setViewMode("month")} 
                className={`px-3 py-1.5 rounded text-sm ${viewMode === "month" ? "bg-amber-600 text-white" : "text-slate-300"}`}
              >
                Mois
              </button>
            </div>
          </div>

          {/* Vue Planning Jour */}
          {viewMode === "day-planning" && (
            <DayPlanningView 
              date={currentDate}
              rdvs={filteredRdvs}
              rappels={myPlanningMode ? rappels : []}
              onRdvMove={handleRdvMove}
              onRdvResize={handleRdvResize}
              loading={loading}
              onViewFullProfile={handleViewFullProfile}
              onRdvDeleted={handleRdvDeleted}
              onSlotClick={(pendingClient || myPlanningMode) ? handleSlotClick : null}
              hasPendingClient={!!pendingClient}
              myPlanningMode={myPlanningMode}
              onRappelUpdated={() => {
                authFetch(`${API}/api/webhook/rappels/semaine?telepro_email=${encodeURIComponent(authUser?.email)}`)
                  .then(r => r.json())
                  .then(data => setRappels(data.rappels || []))
                  .catch(console.error);
              }}
              onViewClientFiche={handleViewFullProfile}
            />
          )}

          {/* Vue Planning Semaine */}
          {viewMode === "week-planning" && (
            <WeekPlanningView 
              weekStart={weekStart}
              rdvs={filteredRdvs}
              rappels={myPlanningMode ? rappels : []}
              onRdvMove={handleRdvMove}
              onRdvResize={handleRdvResize}
              loading={loading}
              onViewFullProfile={handleViewFullProfile}
              onRdvDeleted={handleRdvDeleted}
              onSlotClick={(pendingClient || myPlanningMode) ? handleSlotClick : null}
              hasPendingClient={!!pendingClient}
              myPlanningMode={myPlanningMode}
              onRappelUpdated={() => {
                authFetch(`${API}/api/webhook/rappels/semaine?telepro_email=${encodeURIComponent(authUser?.email)}`)
                  .then(r => r.json())
                  .then(data => setRappels(data.rappels || []))
                  .catch(console.error);
              }}
              onViewClientFiche={handleViewFullProfile}
            />
          )}

          {/* Vue Semaine Simple (ancienne) */}
          {viewMode === "week" && (
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((d, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-slate-400 mb-1">{JOURS[i]}</div>
                  <div 
                    onClick={() => setSelectedDate(d)} 
                    className={`p-2 rounded-lg cursor-pointer ${isToday(d) ? "bg-amber-500/20 ring-2 ring-amber-500" : "bg-slate-700/50"} ${selectedDate.toDateString() === d.toDateString() ? "ring-2 ring-blue-500" : ""}`}
                  >
                    <div className="font-medium text-white">{d.getDate()}</div>
                    {getRdvForDate(d).length > 0 && <div className="text-xs text-amber-400 mt-1">{getRdvForDate(d).length} RDV</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vue Mois */}
          {viewMode === "month" && (
            <>
              <div className="grid grid-cols-7 gap-1 mb-1">{JOURS.map(j => <div key={j} className="text-center text-xs text-slate-400 py-2">{j}</div>)}</div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((d, i) => (
                  <DayCell 
                    key={i} 
                    date={d} 
                    rdvs={getRdvForDate(d)} 
                    isToday={isToday(d)} 
                    isCurrentMonth={isCurrentMonth(d)} 
                    isSelected={selectedDate.toDateString() === d.toDateString()} 
                    onClick={setSelectedDate} 
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Liste des RDV du jour sélectionné (pour les vues simples) */}
        {(viewMode === "week" || viewMode === "month") && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              RDV du {formatDate(selectedDate)} ({selectedRdvs.length})
            </h3>
            {loading ? (
              <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 text-amber-400 animate-spin" /></div>
            ) : selectedRdvs.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {selectedRdvs.map(r => <RdvCard key={r.id} rdv={r} />)}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun RDV ce jour</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de création de RDV */}
      {showCreateRdvModal && pendingClient && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md ring-2 ring-emerald-500/50">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/30 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Créer le RDV R1</h2>
                  <p className="text-sm text-slate-400">
                    {new Date(showCreateRdvModal.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {showCreateRdvModal.time}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Info client */}
              <div className="bg-slate-900/50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase mb-2">Client</p>
                <p className="text-white font-medium">{pendingClient.nom} {pendingClient.prenom}</p>
                <p className="text-sm text-slate-400">{pendingClient.adresse}</p>
                <p className="text-sm text-slate-400">{pendingClient.cp} {pendingClient.ville}</p>
                {pendingClient.telephone && (
                  <button 
                    onClick={() => window.ringoverDial && window.ringoverDial(pendingClient.telephone)}
                    className="text-sm text-emerald-400 hover:text-emerald-300 hover:underline mt-1 flex items-center gap-1"
                  >
                    📞 {pendingClient.telephone}
                  </button>
                )}
              </div>

              {/* Sélection de l'équipe */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Équipe <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedEquipeForRdv}
                  onChange={(e) => { setSelectedEquipeForRdv(e.target.value); setSelectedTechnicien(""); }}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  data-testid="create-rdv-equipe-select"
                >
                  {allTeams.length > 0 ? (
                    allTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name || team.label}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="technicien">🔍 Technicien R1</option>
                      <option value="architecte">📐 Architecte R2</option>
                      <option value="pose">🚚 Équipe Chantier / Pose</option>
                    </>
                  )}
                </select>
              </div>

              {/* Sélection de l'intervenant */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Intervenant <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedTechnicien}
                  onChange={(e) => setSelectedTechnicien(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  data-testid="create-rdv-technicien-select"
                >
                  <option value="">-- Sélectionner --</option>
                  {/* Intervenants basés sur l'équipe sélectionnée */}
                  {selectedEquipeForRdv === 'technicien' && intervenants.techniciens?.map(tech => (
                    <option key={tech} value={tech}>{tech}</option>
                  ))}
                  {selectedEquipeForRdv === 'architecte' && intervenants.architectes?.map(arch => (
                    <option key={arch} value={arch}>{arch}</option>
                  ))}
                  {(selectedEquipeForRdv === 'pose' || selectedEquipeForRdv === 'equipe_chantier') && intervenants.pose?.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                  {/* Membres de l'équipe depuis le Profil Collaborateur */}
                  {teamMembers[selectedEquipeForRdv]?.map(member => (
                    <option key={member.email} value={member.alias}>
                      {member.alias} ({member.name || member.email})
                    </option>
                  ))}
                  {/* Si équipe personnalisée sans membres, permettre la saisie libre */}
                  {!['technicien', 'architecte', 'pose', 'equipe_chantier'].includes(selectedEquipeForRdv) && 
                   (!teamMembers[selectedEquipeForRdv] || teamMembers[selectedEquipeForRdv].length === 0) && (
                    <option value="__saisie_libre__">📝 Saisie libre...</option>
                  )}
                </select>
                {/* Champ de saisie libre pour les équipes personnalisées */}
                {selectedTechnicien === '__saisie_libre__' && (
                  <input
                    type="text"
                    placeholder="Nom de l'intervenant..."
                    onChange={(e) => setSelectedTechnicien(e.target.value)}
                    className="mt-2 w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    autoFocus
                  />
                )}
              </div>

              {/* Résumé */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Résumé du RDV</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-500">Date</p>
                    <p className="text-white">{new Date(showCreateRdvModal.date).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Heure</p>
                    <p className="text-white">{showCreateRdvModal.time}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Équipe</p>
                    <p className="text-white">
                      {allTeams.find(t => t.id === selectedEquipeForRdv)?.name || 
                       allTeams.find(t => t.id === selectedEquipeForRdv)?.label ||
                       selectedEquipeForRdv}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Intervenant</p>
                    <p className="text-white">{selectedTechnicien === '__saisie_libre__' ? '(saisie libre)' : selectedTechnicien || '(à sélectionner)'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-700 flex gap-3">
              <button
                onClick={() => { setShowCreateRdvModal(null); setSelectedTechnicien(""); setSelectedEquipeForRdv("technicien"); }}
                disabled={creatingRdv}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createRdvForPendingClient}
                disabled={creatingRdv || !selectedTechnicien}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-create-rdv-btn"
              >
                {creatingRdv ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {creatingRdv ? 'Création...' : 'Créer le RDV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter un RDV */}
      {showAddRdvModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-700">
            {/* Header */}
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-400" />
                  Ajouter un RDV
                </h2>
                <button 
                  onClick={() => { setShowAddRdvModal(false); setSearchClientQuery(""); setSearchClientResults([]); }}
                  className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-slate-400 text-sm mt-1">Recherchez un client pour lui attribuer un RDV</p>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Recherche client */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchClientQuery}
                  onChange={(e) => {
                    setSearchClientQuery(e.target.value);
                    searchClients(e.target.value);
                  }}
                  placeholder="Rechercher par nom, prénom..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="search-client-for-rdv"
                  autoFocus
                />
                {searchingClients && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                  </div>
                )}
              </div>

              {/* Résultats de recherche */}
              <div className="mt-4 max-h-64 overflow-y-auto">
                {searchClientResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchClientResults.map((client, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectClientForRdv(client)}
                        className="w-full p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-left transition-colors border border-slate-600 hover:border-emerald-500"
                      >
                        <p className="text-white font-medium">
                          {client.data?.['CLIENT NOM'] || client.data?.['NOM'] || ''} {client.data?.['PRÉNOM'] || client.data?.['PRENOM'] || ''}
                        </p>
                        <p className="text-sm text-slate-400">
                          {client.data?.['TEL CLIENT'] || client.data?.['TEL'] || ''} • {client.data?.['VILLE'] || ''}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : searchClientQuery.length >= 2 && !searchingClients ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun client trouvé</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Tapez au moins 2 caractères pour rechercher</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-700">
              <button
                onClick={() => { setShowAddRdvModal(false); setSearchClientQuery(""); setSearchClientResults([]); }}
                className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter un Rappel (Mon Planning) */}
      <AddRappelModal
        isOpen={showAddRappelModal}
        onClose={() => {
          setShowAddRappelModal(false);
          setSelectedSlotForRappel(null);
        }}
        onSuccess={() => {
          // Recharger les rappels
          if (authUser?.email) {
            authFetch(`${API}/api/webhook/rappels/semaine?telepro_email=${encodeURIComponent(authUser.email)}`)
              .then(r => r.json())
              .then(data => setRappels(data.rappels || []))
              .catch(console.error);
          }
          setShowAddRappelModal(false);
          setSelectedSlotForRappel(null);
        }}
        authUser={authUser}
        preSelectedDate={selectedSlotForRappel?.date instanceof Date 
          ? selectedSlotForRappel.date.toISOString().split('T')[0]
          : selectedSlotForRappel?.date}
        preSelectedTime={selectedSlotForRappel?.time}
      />
    </div>
  );
}
